import { BACKEND_URL } from '../config/api';
import axios from 'axios';
/**
 * Push Notification Utilities
 * Handles service worker registration and push subscription
 */

const API = `${BACKEND_URL}/api`;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

/**
 * Check if push notifications are supported
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window;
};

/**
 * Convert VAPID key from base64 to Uint8Array
 */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

/**
 * Register the service worker for push notifications
 */
export const registerServiceWorker = async () => {
  if (!isPushSupported()) {
    return null;
  }
  
  try {
    // Use the unified service worker (handles both caching + push)
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    });
    return registration;
  } catch (error) {
    return null;
  }
};

/**
 * Get existing service worker registration
 */
export const getServiceWorkerRegistration = async () => {
  if (!isPushSupported()) {
    return null;
  }
  
  return await navigator.serviceWorker.ready;
};

/**
 * Request notification permission
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    return 'denied';
  }
  
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  
  const permission = await Notification.requestPermission();
  return permission;
};

/**
 * Subscribe to push notifications
 */
export const subscribeToPush = async () => {
  if (!isPushSupported()) {
    throw new Error('Push notifications not supported');
  }
  
  // Request permission first
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Notification permission denied');
  }
  
  // Get service worker registration
  const registration = await getServiceWorkerRegistration();
  if (!registration) {
    // Try to register
    const newReg = await registerServiceWorker();
    if (!newReg) {
      throw new Error('Service Worker registration failed');
    }
  }
  
  const swReg = await navigator.serviceWorker.ready;
  
  // Get VAPID public key from environment or fetch from server
  let vapidKey = VAPID_PUBLIC_KEY;
  
  if (!vapidKey) {
    try {
      const response = await axios.get(`${API}/push/vapid-public-key`, { withCredentials: true });
      vapidKey = response.data.publicKey;
    } catch (error) {
      console.error('[PUSH] Failed to get VAPID key:', error);
      throw new Error('Failed to get VAPID key');
    }
  }
  
  // Check for existing subscription
  let subscription = await swReg.pushManager.getSubscription();
  
  if (!subscription) {
    // Subscribe to push
    try {
      subscription = await swReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidKey)
      });
    } catch (error) {
      console.error('[PUSH] Push subscription failed:', error);
      throw error;
    }
  }
  
  // Send subscription to server
  const subscriptionData = subscription.toJSON();
  
  try {
    await axios.post(`${API}/push/subscribe`, {
      endpoint: subscriptionData.endpoint,
      keys: subscriptionData.keys
    }, { withCredentials: true });
    
    return subscription;
  } catch (error) {
    console.error('[PUSH] Failed to save subscription:', error);
    throw error;
  }
};

/**
 * Unsubscribe from push notifications
 */
export const unsubscribeFromPush = async () => {
  if (!isPushSupported()) {
    return false;
  }
  
  const swReg = await navigator.serviceWorker.ready;
  const subscription = await swReg.pushManager.getSubscription();
  
  if (!subscription) {
    return true;
  }
  
  try {
    // Unsubscribe on server
    const subscriptionData = subscription.toJSON();
    await axios.post(`${API}/push/unsubscribe`, {
      endpoint: subscriptionData.endpoint,
      keys: subscriptionData.keys
    }, { withCredentials: true });
    
    // Unsubscribe locally
    await subscription.unsubscribe();
    
    return true;
  } catch (error) {
    console.error('[PUSH] Failed to unsubscribe:', error);
    return false;
  }
};

/**
 * Check if user is subscribed to push notifications
 */
export const isPushSubscribed = async () => {
  if (!isPushSupported()) {
    return false;
  }
  
  try {
    const swReg = await navigator.serviceWorker.ready;
    const subscription = await swReg.pushManager.getSubscription();
    return !!subscription;
  } catch (error) {
    return false;
  }
};

/**
 * Get push notification permission state
 */
export const getPushPermissionState = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission;
};
